import { useCallback } from 'react';
import { Logger } from 'utils/logger';
import { IAddressComponent, IAddressDetail, IGeometry } from 'types';

interface IGeocodeResult {
  address_components: IAddressComponent[];
  formatted_address: string;
  place_id: string;
  types: string[];
  geometry: IGeometry;
}

export const useGooglePlaces = () => {
  const getAddressObject = useCallback(
    (addressComponents: IAddressComponent[]) => {
      const ShouldBeComponent: Record<string, string[]> = {
        street_number: ['street_number'],
        postal_code: ['postal_code'],
        street: ['street_address', 'route', 'political'],
        province: [
          'administrative_area_level_1',
          'administrative_area_level_2',
          'administrative_area_level_3',
          'administrative_area_level_4',
          'administrative_area_level_5',
        ],
        city: [
          'locality',
          'sublocality',
          'sublocality_level_1',
          'sublocality_level_2',
          'sublocality_level_3',
          'sublocality_level_4',
        ],
        country: ['country'],
      };

      let streetNumber = '';
      let street = '';
      const detail: Partial<IAddressDetail> = {
        postal_code: '',
        province: '',
        city: '',
        country: '',
        address: '',
      };

      addressComponents.forEach((component) => {
        for (const key in ShouldBeComponent) {
          if (ShouldBeComponent[key].includes(component.types[0])) {
            if (key === 'street_number') {
              streetNumber = component.long_name;
            } else if (key === 'street') {
              street = component.long_name;
            } else if (key === 'postal_code') {
              detail.postal_code = component.long_name;
            } else if (key === 'province') {
              detail.province = component.long_name;
            } else if (key === 'city') {
              detail.city = component.long_name;
            } else if (key === 'country') {
              detail.country = component.long_name;
            }
          }
        }
      });

      detail.address = `${streetNumber} ${street}`.trim();

      if (detail.country === 'US') {
        detail.state = detail.province;
        delete detail.province;
      }

      return detail as IAddressDetail;
    },
    [],
  );

  const getGeocodeByLatLng = useCallback(
    async (lat: number, lng: number): Promise<IGeocodeResult[] | null> => {
      try {
        // Use the Google Maps JavaScript API Geocoder service instead of direct HTTP request
        const geocoder = new google.maps.Geocoder();

        return new Promise((resolve, _) => {
          geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === google.maps.GeocoderStatus.OK && results) {
              // Transform the results to match the expected format
              const transformedResults = results.map((result) => ({
                address_components: result.address_components,
                formatted_address: result.formatted_address,
                place_id: result.place_id,
                types: result.types,
                geometry: {
                  location: {
                    lat: result.geometry.location.lat(),
                    lng: result.geometry.location.lng(),
                  },
                  location_type: result.geometry.location_type,
                  viewport: result.geometry.viewport,
                  bounds: result.geometry.bounds,
                },
              }));

              resolve(transformedResults as IGeocodeResult[]);
            } else {
              Logger.error({ message: 'Geocode API error: ', data: status });
              resolve(null);
            }
          });
        });
      } catch (error) {
        Logger.error({ message: 'Error geocoding coordinates: ', data: error });
        return null;
      }
    },
    [],
  );

  return {
    getAddressObject,
    getGeocodeByLatLng,
  };
};
